import { Button, Form } from '@heroui/react'
import { ApiError, useAcceptResellerInvitation } from '@qse/api'
import { AppRoutesList, validatePassword, validatePasswordConfirmation } from '@qse/constants'
import { HeroInput } from '@qse/ui'
import { detectCurrentApp, getDefaultRoute } from '@qse/utils'
import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { authService } from '../logic'

const CompleteResellerAccount = () => {
  const { invitationData, invitationCode } = useLocation().state
  const navigate = useNavigate()
  const acceptInvitationMutation = useAcceptResellerInvitation()

  const [password, setPassword] = useState('')
  const [retypePassword, setRetypePassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const email = invitationData?.email || ''
  const firstName = invitationData?.firstName || ''
  const lastName = invitationData?.lastName || ''

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      if (!invitationCode) {
        toast.error('Invalid invitation. Please request a new invitation.')
        return
      }

      await acceptInvitationMutation.mutateAsync({
        invitationCode,
        password,
      })

      // Auto-login after successful account creation
      try {
        const appDetection = detectCurrentApp()
        const domain = appDetection.appType === 'admin' ? 'admin' : 'dashboard'

        const loginResult = await authService.login(email, password, domain)

        if (loginResult.requiresTOTPSetup) {
          navigate(AppRoutesList.twoFA, {
            replace: true,
            state: {
              isFirstLogin: loginResult.isFirstLogin,
              totpSetup: loginResult.totpSetup,
            },
          })
        } else if (loginResult.requiresTOTPCode) {
          navigate(AppRoutesList.twoFAVerify, {
            replace: true,
            state: { totpSetup: loginResult.totpSetup },
          })
        } else if (loginResult.user) {
          const { appType } = detectCurrentApp()
          const defaultRoute = getDefaultRoute(loginResult.user.userRole, appType)
          navigate(defaultRoute, { replace: true })
        } else {
          navigate(AppRoutesList.login, { replace: true })
        }
      } catch (loginError) {
        console.error('Auto-login failed after reseller account creation:', loginError)
        toast.success('Account created successfully! Please log in.')
        navigate(AppRoutesList.login)
      }
    } catch (error) {
      if (error instanceof ApiError) {
        const backendError = error.response?.data?.error ?? error.response?.data ?? {}
        const backendMessage = backendError.message?.toString() ?? ''
        toast.error(backendMessage || 'Failed to create account. Please try again.')
      } else {
        const fallbackMessage =
          error instanceof Error
            ? error.message
            : 'Failed to complete account setup. Please try again later.'
        toast.error(fallbackMessage)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="h-full">
      <div className="flex items-center mb-40 mt-20 pb-24 h-full w-full lg:pl-[110px] lg:max-w-full max-w-[440px] mx-auto">
        <div className=" lg:w-[440px] w-full p-8 lg:p-0 flex flex-col gap-11">
          <div className="flex flex-col gap-2 ">
            <h1 className="text-[28px] font-medium">Complete Your Reseller Account</h1>
            <p className=" text-[15px]">Please set your password to activate your account:</p>
          </div>
          <Form className="w-full" validationBehavior="native" onSubmit={handleSubmit}>
            <div className="w-full">
              <div className="grid grid-cols-2 gap-[30px]">
                <div>
                  <HeroInput
                    isDisabled
                    isRequired
                    control="text"
                    label="First Name"
                    name="firstName"
                    placeholder="First Name"
                    radius="sm"
                    size="md"
                    value={firstName}
                    variant="bordered"
                  />
                </div>
                <div>
                  <HeroInput
                    isDisabled
                    isRequired
                    control="text"
                    label="Last Name"
                    name="lastName"
                    placeholder="Last Name"
                    radius="sm"
                    size="md"
                    value={lastName}
                    variant="bordered"
                  />
                </div>
                <div className="col-span-2">
                  <HeroInput
                    isDisabled
                    isRequired
                    control="email"
                    label="Email"
                    name="email"
                    placeholder="Email"
                    radius="sm"
                    size="md"
                    value={email}
                    variant="bordered"
                  />
                </div>

                <div className="col-span-2">
                  <HeroInput
                    isRequired
                    control="password"
                    label="Password"
                    name="password"
                    placeholder="Password"
                    radius="sm"
                    size="md"
                    validate={validatePassword}
                    value={password}
                    variant="bordered"
                    onChange={setPassword}
                  />
                </div>

                <div className="col-span-2">
                  <HeroInput
                    isRequired
                    control="password"
                    label="Re-type password"
                    name="retypePassword"
                    placeholder="Re-type password"
                    radius="sm"
                    size="md"
                    validate={validatePasswordConfirmation(password)}
                    value={retypePassword}
                    variant="bordered"
                    onChange={setRetypePassword}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-[30px] mt-[30px]">
                <span className="text-xs font-light">
                  By creating your account, you accept and agree to the{' '}
                  <span className="underline ">
                    <Link target="_blank" to="https://qse.group/terms-conditions/">
                      Terms{' '}
                    </Link>{' '}
                    and{' '}
                    <Link target="_blank" to="https://qse.group/terms-conditions/">
                      Conditions
                    </Link>
                    .
                  </span>
                </span>
              </div>
              <Button
                className="w-full mt-5 font-medium"
                color="primary"
                isLoading={isLoading}
                radius="sm"
                type="submit"
              >
                Create Account
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default CompleteResellerAccount
